import { useState } from 'react';
import { Button, ConfirmDialog } from '../../components';
import { notify } from '../../services/notify';
import type { ProfileStatus } from './api';
import { useSession } from '../../lib/auth/useSession';
import { useDeactivateProfile } from './queries';

/**
 * Deactivate, shared by the Chef and Eater dialogs: confirm with a required
 * reason, then `POST /admin/profiles/{id}/deactivate`, which sets
 * `Profile.status = inactive` and stores the reason as an admin note.
 * Hidden once the profile is already inactive.
 */
export function DeactivateProfileButton({
  profileId,
  status,
  noun,
  onDone,
}: {
  profileId: string;
  status: ProfileStatus;
  /** "chef" / "eater" — used in the dialog title and toast. */
  noun: string;
  onDone: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const deactivate = useDeactivateProfile(profileId);
  const { locked } = useSession();

  if (status === 'inactive') return null;

  return (
    <>
      <Button size="sm" lockedReason={locked('crm.write')} onClick={() => setConfirming(true)}>
        Deactivate
      </Button>
      {confirming && (
        <ConfirmDialog
          title={`Deactivate this ${noun}?`}
          body="The account is set to inactive. The reason is kept as an admin note."
          confirmLabel="Deactivate"
          requireReason
          busy={deactivate.isPending}
          onClose={() => setConfirming(false)}
          onConfirm={(reason) =>
            deactivate.mutate(reason, {
              onSuccess: () => {
                notify.success(`${noun.charAt(0).toUpperCase()}${noun.slice(1)} account deactivated.`);
                setConfirming(false);
                onDone();
              },
            })
          }
        />
      )}
    </>
  );
}
